// src/lib/petaEsokService.ts
//
// BekalNanti — Milestone P7: PetaEsok Core Trajectory Service
// 100% Deterministic Mapping Adapter. ZERO AI, ZERO formula re-implementation.
// P7 answers WHEN, WHERE, WHAT by consuming P5 calculation results and P6 reality check results.

import { GoalRecord, JourneyData, PersonRecord } from '../journey/types'
import { isEducationGoal, GOAL_CAPABILITIES } from './goalCapability'
import { runGoalCalculation } from './calculationService'
import { evaluatePlanReality } from './realityCheckService'
import { formatIDR } from './financialCalculation'
import { formatRelationship } from './peopleService'
import { getGoalDomain } from './goalDomainRegistry'
import { CalculationResult } from './calculation/types'
import { RealityCheckResult } from './realityCheckTypes'
import {
  PetaEsokTrajectoryMap,
  PetaEsokTimelineMilestone,
  PetaEsokPressurePoint,
  PetaEsokUnknownItem,
  PetaEsokRouteOption,
  PetaEsokRouteId,
} from './petaEsokTypes'

/**
 * Builds the canonical PetaEsok trajectory map for a given goal and household journey.
 * Strictly adheres to P7 governance boundaries:
 * - Does NOT calculate new financial numbers (consumes P5 & P6).
 * - Distinguishes confirmed vs estimated funding without conflation.
 * - Leaves unknowns explicit without invention.
 * - Reflects non-education goals with honest capability disclosures.
 * - Exposes routes without algorithmic auto-selection.
 */
export function buildPetaEsokTrajectory(
  goal: GoalRecord,
  journey: JourneyData,
  selectedRouteId: PetaEsokRouteId = 'primary'
): PetaEsokTrajectoryMap {
  const currentYear = new Date().getFullYear()

  // 1. Resolve Person Context
  let person: PersonRecord | null = null
  if (goal.personId) {
    person = journey.persons.find((p) => p.id === goal.personId) || null
  }
  const personName = person ? person.fullName : 'Keluarga Bersama'
  const personRelationship = person ? formatRelationship(person.relationship) : null

  // 2. Consume Canonical P6 Reality Check
  const p6: RealityCheckResult = evaluatePlanReality(goal, journey)

  const isEdu = isEducationGoal(goal.goalType)
  const hasTarget = Boolean(goal.target)
  const hasCostItems = (goal.costItems || []).length > 0
  const householdCapacity = journey.financialReality?.summary.netMonthlyCapacity ?? 0
  const liquidSavings =
    journey.financialReality?.summary.availableSavings ??
    journey.financialReality?.summary.totalSavings ??
    0

  // 3. Handle Goals with No Target and No Cost Data Yet
  if (!isEdu && !hasTarget && !hasCostItems) {
    const capability = GOAL_CAPABILITIES[goal.goalType]
    const nonEduNotice =
      capability?.statusDescription ||
      'Modul perencanaan trajektori khusus untuk kategori sasaran ini belum aktif. Data Anda tersimpan aman.'

    return {
      goalId: goal.id,
      goalName: goal.name,
      goalType: goal.goalType,
      isEducation: false,
      personId: goal.personId || null,
      personName,
      personRelationship,

      currentYear,
      planningStateLabel: 'Kategori Tersimpan Aman',
      currentRealitySummary: `Sasaran ${goal.name} tercatat dalam portofolio keluarga untuk ${personName}.`,
      householdCapacityLabel: householdCapacity > 0 ? `${formatIDR(householdCapacity)} / bulan` : 'Belum tercatat',
      availableSavingsLabel: liquidSavings > 0 ? formatIDR(liquidSavings) : 'Belum tercatat',

      targetHorizonYear: null,
      yearsRemaining: null,
      monthsRemaining: null,
      targetSummary: 'Spesifikasi target belum dibuka untuk tipe rencana ini.',
      targetInstitutionOrProgram: null,

      milestones: [
        {
          id: 'ms-now',
          year: currentYear,
          title: 'Posisi Saat Ini',
          description: `Sasaran ${goal.name} dibuat dan terhubung ke profil ${personName}.`,
          type: 'current_position',
          status: 'active',
        },
        {
          id: 'ms-future',
          year: currentYear + 1,
          title: 'Pemetaan Mendatang',
          description: 'Spesifikasi milestone dan trajektori bertahap akan dibuka pada modul rilis mendatang.',
          type: 'transition',
          status: 'upcoming',
        },
      ],

      realityCheckStatus: p6.status,
      realityCheckStatusLabel: p6.statusLabel,
      realityCheckHeadline: p6.headline,
      realityCheckSummary: p6.summaryStatement,
      signals: p6.signals,
      uncertainties: p6.uncertainties,

      bekal: {
        totalProjectedNeed: 0,
        totalNominalNeed: 0,
        confirmedFundingTotal: 0,
        estimatedFundingTotal: 0,
        unknownFundingCount: 0,
        unknownCostCount: 0,
        confirmedFundingSources: [],
        estimatedFundingSources: [],
        costItems: [],
        requiredMonthlySavingConfirmed: 0,
        requiredMonthlySavingEstimated: 0,
      },

      pressurePoints: [],
      unknowns: [
        {
          id: 'unk-non-edu-spec',
          category: 'prerequisite',
          title: 'Sasaran & Biaya Belum Dirinci',
          description: nonEduNotice,
          impact: 'Lengkapi tahun target dan komponen biaya agar linimasa dan kebutuhan dana dapat dihitung.',
        },
      ],

      routes: [
        {
          id: 'primary',
          title: 'Jalur Standar (Tersimpan)',
          label: 'Standar',
          description: 'Rencana tersimpan aman menunggu ketersediaan modul trajektori spesifik.',
          isPrimary: true,
          differencesSummary: 'Belum ada alternatif jalur komparatif.',
          tradeOffs: ['Data tersimpan aman tanpa kalkulasi fiktif.'],
        },
      ],
      selectedRouteId: 'primary',

      isCalculable: false,
      nonEducationNotice: nonEduNotice,
    }
  }

  // 4. Consume Canonical P5 Calculation Result for Goal
  const p5: CalculationResult = runGoalCalculation(goal, journey)

  const targetYear = p5.assumptions.targetYear || (goal.target?.targetYear ?? null)
  const yearsRemaining = p5.assumptions.yearsUntilTarget > 0 ? p5.assumptions.yearsUntilTarget : null
  const monthsRemaining = p5.assumptions.monthsUntilTarget > 0 ? p5.assumptions.monthsUntilTarget : null

  // Target summary details
  const domain = getGoalDomain(goal.goalType)
  const targetLevel = goal.target?.targetStage
    ? domain.stages.find((s) => s.id === goal.target?.targetStage)?.label || goal.target?.targetStage
    : domain.title
  const targetField = goal.target?.programName || ''
  const targetCity = goal.target?.targetCity || goal.target?.institutionName || ''
  const targetSummary = [targetLevel, targetField, targetCity ? `di ${targetCity}` : '']
    .filter(Boolean)
    .join(' • ') || `Target ${domain.title.toLowerCase()} belum ditentukan`

  // Bekal mapping strictly matching P5
  const confirmedFundingTotal = p5.funding.confirmedFunding
  const estimatedFundingTotal = p5.funding.estimatedFunding
  const confirmedFundingSources = (goal.fundingSources || []).filter((f) => f.isConfirmed || f.status === 'confirmed')
  const estimatedFundingSources = (goal.fundingSources || []).filter((f) => !f.isConfirmed && f.status !== 'confirmed')
  const costItems = goal.costItems || []

  // 5. Build Trajectory Milestones (WHEN & WHAT)
  const milestones: PetaEsokTimelineMilestone[] = []

  // Milestone A: Where I Am Now
  milestones.push({
    id: 'ms-current',
    year: currentYear,
    dateLabel: `Tahun ${currentYear} (Saat Ini)`,
    title: 'Titik Awal Perjalanan',
    description: `Persiapan bekal ${goal.name} untuk ${personName}. Kapasitas kas bulanan keluarga: ${
      householdCapacity > 0 ? formatIDR(householdCapacity) : 'Belum diisi'
    }.`,
    type: 'current_position',
    status: 'active',
  })

  // Milestone B: Mid-Way Checkpoint (if target is at least 2 years out)
  if (targetYear && targetYear - currentYear >= 2) {
    const midYear = Math.floor(currentYear + (targetYear - currentYear) / 2)
    milestones.push({
      id: 'ms-midpoint',
      year: midYear,
      dateLabel: `Tahun ${midYear} (Paruh Waktu)`,
      title: 'Evaluasi & Akumulasi Bekal',
      description: `Evaluasi realisasi tabungan terencana dan kepastian sumber pendanaan sebelum memasuki tahun pelaksanaan.`,
      type: 'funding_checkpoint',
      status: 'upcoming',
    })
  }

  // Milestone C: Application & Entry Horizon (Target Year)
  if (targetYear) {
    milestones.push({
      id: 'ms-target',
      year: targetYear,
      dateLabel: `Tahun ${targetYear} (Mulai Sasaran)`,
      title: `Titik Mulai: ${targetLevel}`,
      description: `Titik mulai realisasi kebutuhan bekal ${goal.name} ${personName} (${targetSummary}).`,
      type: 'target_event',
      status: 'upcoming',
      amount: p5.costs.totalProjectedCost > 0 ? p5.costs.totalProjectedCost : null,
      currency: 'IDR',
    })

    // Milestone D: Completion / Stabilization Horizon
    const durationYears = p5.assumptions.studyDurationYears || 4
    const completionYear = targetYear + durationYears
    milestones.push({
      id: 'ms-completion',
      year: completionYear,
      dateLabel: `Tahun ${completionYear} (Target Tercapai)`,
      title: `Penyelesaian & Kemandirian ${targetLevel}`,
      description: `Target penyelesaian masa persiapan/pelaksanaan (${durationYears} tahun) dan transisi ke tahap kemandirian berikutnya.`,
      type: 'transition',
      status: 'upcoming',
    })
  }

  // 6. Map Pressure Points Factual from P5/P6
  const pressurePoints: PetaEsokPressurePoint[] = []

  if (p6.snapshot.hasCapacityDeficit && p6.snapshot.requiredMonthlySavingConfirmedGap > 0) {
    pressurePoints.push({
      id: 'pp-capacity',
      timingLabel: `Setiap Bulan (${currentYear} s.d. ${targetYear || currentYear})`,
      title: 'Tekanan Arus Kas Bulanan',
      description: `Kebutuhan menabung (${formatIDR(
        p6.snapshot.requiredMonthlySavingConfirmedGap
      )}/bln) melampaui sisa kapasitas arus kas rumah tangga (${formatIDR(householdCapacity)}/bln) sebesar ${formatIDR(
        Math.abs(p6.snapshot.monthlyCapacityDifference)
      )}/bln.`,
      severity: 'high',
      sourceMetric: 'P6.snapshot.monthlyCapacityDifference',
    })
  }

  if (p5.gap.gapAfterConfirmedFunding > 0 && p5.gap.gapAfterEstimatedFunding === 0 && estimatedFundingTotal > 0) {
    pressurePoints.push({
      id: 'pp-unconfirmed-dependency',
      timingLabel: `Sebelum Masuk (${targetYear || currentYear})`,
      title: 'Ketergantungan Pada Pendanaan Estimasi',
      description: `Rencana sangat bergantung pada kepastian sumber dana estimasi (misal beasiswa/bantuan) sebesar ${formatIDR(
        estimatedFundingTotal
      )}. Jika dana ini tidak terealisasi, defisit biaya tetap ada.`,
      severity: 'medium',
      sourceMetric: 'P5.funding.estimatedFunding',
    })
  }

  if (p5.costs.unknownCostCount > 0) {
    pressurePoints.push({
      id: 'pp-unknown-costs',
      timingLabel: 'Tahap Perencanaan Awal',
      title: `${p5.costs.unknownCostCount} Komponen Biaya Belum Diketahui`,
      description: 'Terdapat komponen biaya yang tercatat namun belum memiliki nominal pasti, sehingga proyeksi biaya total masih berpotensi bertambah.',
      severity: 'medium',
      sourceMetric: 'P5.costs.unknownCostCount',
    })
  }

  // 7. Map Known Unknowns Explicitly
  const unknowns: PetaEsokUnknownItem[] = []

  if (p5.costs.unknownCostCount > 0) {
    const unknownCosts = (goal.costItems || []).filter((c) => c.certainty === 'unknown')
    unknownCosts.forEach((c) => {
      unknowns.push({
        id: `unk-cost-${c.id}`,
        category: 'cost',
        title: `Nominal ${c.name} Belum Dipastikan`,
        description: `Komponen biaya kategori ${c.category} dimasukkan dengan tingkat kepastian belum diketahui.`,
        impact: 'Dapat meningkatkan total proyeksi bekal saat nominal diverifikasi.',
      })
    })
  }

  if (p5.funding.unknownFundingCount > 0) {
    const unknownFunds = (goal.fundingSources || []).filter((f) => f.certainty === 'unknown')
    unknownFunds.forEach((f) => {
      unknowns.push({
        id: `unk-fund-${f.id}`,
        category: 'funding',
        title: `Potensi Dana ${f.name} Belum Dipastikan`,
        description: `Sumber dana tercatat namun belum dapat dipastikan nominal atau syarat kelulusannya.`,
        impact: 'Belum diperhitungkan sebagai pengurang defisit biaya.',
      })
    })
  }

  // 8. Legitimate Route Options (User Chooses — Never Auto-Selected)
  const routes: PetaEsokRouteOption[] = [
    {
      id: 'primary',
      title: 'Jalur 1: Mandiri Terkonfirmasi',
      label: 'Fokus Dana Pasti',
      description: 'Mengandalkan kapasitas tabungan keluarga dan sumber dana yang sudah pasti ada tanpa spekulasi.',
      isPrimary: true,
      differencesSummary: `Kebutuhan menabung bulanan: ${formatIDR(
        p5.savingRequirement.requiredMonthlySavingConfirmedGap
      )}/bln untuk menutup defisit ${formatIDR(p5.gap.gapAfterConfirmedFunding)}.`,
      tradeOffs: [
        'Tingkat kepastian rencana paling tinggi.',
        'Memerlukan alokasi arus kas rumah tangga yang lebih ketat.',
      ],
    },
  ]

  if (estimatedFundingTotal > 0) {
    routes.push({
      id: 'alternative_scholarship',
      title: 'Jalur 2: Kombinasi Potensi Pendanaan / Beasiswa',
      label: 'Dengan Potensi Dana',
      description: 'Menargetkan beasiswa atau hibah pendanaan estimasi untuk meringankan beban tabungan keluarga.',
      isPrimary: false,
      differencesSummary: `Jika dana estimasi ${formatIDR(
        estimatedFundingTotal
      )} terwujud, tabungan bulanan berkurang menjadi ${formatIDR(
        p5.savingRequirement.requiredMonthlySavingEstimatedGap
      )}/bln.`,
      tradeOffs: [
        'Beban tabungan keluarga jauh lebih ringan.',
        'Memerlukan persiapan syarat seleksi / kepastian dana sedini mungkin.',
      ],
    })
  }

  if (targetYear && yearsRemaining && yearsRemaining < 4) {
    routes.push({
      id: 'extended_horizon',
      title: 'Jalur 3: Horizon Fleksibel / Gap Year',
      label: 'Penyesuaian Waktu',
      description: 'Menambah masa persiapan 1 tahun untuk memperpanjang waktu akumulasi dana tabungan.',
      isPrimary: false,
      differencesSummary: 'Memperpanjang jangka waktu menabung sehingga angsuran per bulan menjadi lebih terjangkau.',
      tradeOffs: [
        'Angsuran tabungan bulanan menjadi lebih ringan.',
        'Waktu memulai jenjang pendidikan tertunda 1 tahun.',
      ],
    })
  }

  return {
    goalId: goal.id,
    goalName: goal.name,
    goalType: goal.goalType,
    isEducation: true,
    personId: goal.personId || null,
    personName,
    personRelationship,

    currentYear,
    planningStateLabel: p5.isCalculable ? 'Trajektori Terpetakan' : 'Input Parsial',
    currentRealitySummary: `Persiapan bekal pendidikan ${personName} menuju ${targetLevel}.`,
    householdCapacityLabel: householdCapacity > 0 ? `${formatIDR(householdCapacity)} / bulan` : 'Belum diisi',
    availableSavingsLabel: liquidSavings > 0 ? formatIDR(liquidSavings) : 'Rp 0',

    targetHorizonYear: targetYear,
    yearsRemaining,
    monthsRemaining,
    targetSummary,
    targetInstitutionOrProgram: goal.target?.programName || goal.target?.institutionName || null,

    milestones,

    realityCheckStatus: p6.status,
    realityCheckStatusLabel: p6.statusLabel,
    realityCheckHeadline: p6.headline,
    realityCheckSummary: p6.summaryStatement,
    signals: p6.signals,
    uncertainties: p6.uncertainties,

    bekal: {
      totalProjectedNeed: p5.costs.totalProjectedCost,
      totalNominalNeed: p5.costs.totalNominalCost,
      confirmedFundingTotal,
      estimatedFundingTotal,
      unknownFundingCount: p5.funding.unknownFundingCount,
      unknownCostCount: p5.costs.unknownCostCount,
      confirmedFundingSources,
      estimatedFundingSources,
      costItems,
      requiredMonthlySavingConfirmed: p5.savingRequirement.requiredMonthlySavingConfirmedGap,
      requiredMonthlySavingEstimated: p5.savingRequirement.requiredMonthlySavingEstimatedGap,
    },

    pressurePoints,
    unknowns,

    routes,
    selectedRouteId,

    isCalculable: p5.isCalculable,
    nonEducationNotice: null,
  }
}
